/**
 * App Provider Composition
 * Bulletproof React pattern for provider composition
 *
 * This file composes all application providers in the correct order:
 * 1. HelmetProvider (Document head management)
 * 2. ErrorBoundary (Error handling)
 * 3. QueryClientProvider (React Query)
 * 4. AccessibilityProvider (Accessibility settings)
 * 5. AuthLoader (Authentication with react-query-auth)
 * 6. Global UI components (Toaster, Analytics, etc.)
 *
 * Note: BreadcrumbProvider is located in AppRouter (router.tsx) because
 * it requires router context (useMatches, useLocation) to function properly.
 */

import React, { Suspense } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Analytics } from '@vercel/analytics/react';
import { queryClient } from '@/lib/react-query';
import { AuthLoader } from '@/lib/auth';
import { AccessibilityProvider } from '@/providers';
import { ErrorBoundary } from '@/components/errors/error-boundary';
import { useCookieConsent } from '@/hooks/use-cookie-consent';
import { CookieBanner } from '@/components/cookie/cookie-banner';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';

/**
 * Loading component shown while auth is initializing
 */
function AuthLoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <div className="mx-auto mb-4 size-12 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
        <p className="text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
}

/**
 * Error component shown when auth initialization fails
 */
function AuthErrorFallback({ error }: { error: Error }) {
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="max-w-md rounded-lg border border-destructive bg-destructive/10 p-6 text-center">
        <h2 className="mb-2 text-xl font-semibold text-destructive">
          Authentication Error
        </h2>
        <p className="mb-4 text-muted-foreground">
          Failed to initialize authentication. Please try refreshing the page.
        </p>
        <details className="mb-4 text-left text-sm">
          <summary className="cursor-pointer font-medium">Error details</summary>
          <pre className="mt-2 overflow-auto rounded bg-muted p-2 text-xs">
            {error.message}
          </pre>
        </details>
        <button
          onClick={() => window.location.reload()}
          className="rounded bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
        >
          Refresh Page
        </button>
      </div>
    </div>
  );
}

/**
 * Suspense fallback for lazy-loaded content
 */
function SuspenseFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <div className="mx-auto mb-4 size-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
}

/**
 * Analytics component that respects cookie consent preferences
 * Only loads Vercel Analytics if user has granted analytics consent
 */
function ConsentAwareAnalytics() {
  const { consent } = useCookieConsent();

  if (consent?.cookieCategories.analytics) {
    return <Analytics />;
  }

  return null;
}

/**
 * Main App Provider
 * Composes all application-level providers
 *
 * Provider hierarchy (outermost to innermost):
 * 1. HelmetProvider - Document head management
 * 2. ErrorBoundary - Catches all unhandled errors
 * 3. QueryClientProvider - React Query state management
 * 4. AccessibilityProvider - Accessibility preferences
 * 5. TooltipProvider - Global tooltip context
 * 6. AuthLoader - Authentication state (only wraps route content)
 * 7. Suspense - Lazy loading boundary
 *
 * Note: Global UI components (Toaster, CookieBanner, etc.) are rendered
 * outside AuthLoader so they display for both authenticated and unauthenticated users.
 */
export function AppProvider({ children }: { children: React.ReactNode }) {
  return (
    <HelmetProvider>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          {/* React Query Devtools (only in development) */}
          {import.meta.env.DEV && (
            <ReactQueryDevtools initialIsOpen={false} position="bottom" />
          )}

          {/* Accessibility Provider */}
          <AccessibilityProvider>
            {/* Tooltip Provider */}
            <TooltipProvider>
              {/* Auth Provider using react-query-auth */}
              <AuthLoader
                renderLoading={() => <AuthLoadingFallback />}
                renderUnauthenticated={() => <>{children}</>}
                renderError={(error) => <AuthErrorFallback error={error instanceof Error ? error : new Error(String(error))} />}
              >
                {/* Suspense boundary for lazy-loaded routes */}
                <Suspense fallback={<SuspenseFallback />}>
                  {children}
                </Suspense>
              </AuthLoader>

              {/* Global UI Components - Outside AuthLoader so they render for all users */}
              <Toaster />
              <Sonner />
              <ConsentAwareAnalytics />
              <CookieBanner />
            </TooltipProvider>
          </AccessibilityProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </HelmetProvider>
  );
}
